import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion } from 'motion/react';
import Navbar from './components/Navbar';
import TopBar from './components/TopBar';
import Banner from './components/Banner';
import TopSelectedArticles from './components/TopSelectedArticles';
import SecondaryHeroSlider from './components/SecondaryHeroSlider';
import FieldOfRevolutionSection from './components/FieldOfRevolutionSection';
import FeaturedCategories from './components/FeaturedCategories';
import Section from './components/Section';
import VideoLibrary from './components/VideoLibrary';
import Footer from './components/Footer';
import HomeNewsletter from './components/HomeNewsletter';
import { useTranslate } from './context/LanguageContext';
import { useData } from './context/DataContext';
import { Article } from './types';
import { API_BASE_URL } from './api';

// Lazy-loaded pages — only downloaded when the user visits that route
const ArticlesPage    = lazy(() => import('./pages/Articles'));
const ArticleDetail   = lazy(() => import('./pages/ArticleDetail'));
const Author          = lazy(() => import('./pages/Author'));
const Videos          = lazy(() => import('./pages/Videos'));
const About           = lazy(() => import('./pages/About'));
const Contact         = lazy(() => import('./pages/Contact'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse      = lazy(() => import('./pages/TermsOfUse'));
const EditorialPolicy = lazy(() => import('./pages/EditorialPolicy'));
const AdminDashboard  = lazy(() => import('./pages/AdminDashboard'));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#1a365d] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Normalizes a category string to make comparisons extremely robust across
 * Arabic dialects, spelling styles, definite articles, spaces, and English casings.
 */
function normalizeCategory(str: string): string {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .toLowerCase()
    .split(/[\s\-_]+/)
    .map(word => word.replace(/^ال/, ''))
    .join('')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي');
}

/**
 * Matches an article against an Arabic category name.
 * Checks both `category` (Arabic DB field) and `category_en` (English DB field)
 * as well as common alternate spellings / plural forms.
 */
function matchCategory(article: Article, arabicName: string, englishNames: string[]): boolean {
  const artCatNorm = normalizeCategory(article.category || '');
  const artCatEnNorm = normalizeCategory(article.category_en || '');

  const targetArNorm = normalizeCategory(arabicName);
  const targetEnNorms = englishNames.map(en => normalizeCategory(en));

  if (artCatNorm === targetArNorm) return true;
  if (targetEnNorms.includes(artCatNorm)) return true;
  if (targetEnNorms.includes(artCatEnNorm)) return true;

  // Partial match fallback (e.g. if the category name contains or is contained in the target)
  if (artCatNorm && targetArNorm && (artCatNorm.includes(targetArNorm) || targetArNorm.includes(artCatNorm))) return true;

  return false;
}

export default function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { dir, translateArticles } = useTranslate();
  const { articles: rawArticles, videos, isLoading, settings } = useData();

  const articles = translateArticles(rawArticles || []);

  // Update favicon based on settings logo
  useEffect(() => {
    if (settings?.logo_url) {
      const logoUrl = settings.logo_url.startsWith('http') 
        ? settings.logo_url 
        : `${API_BASE_URL.replace('/api', '')}${settings.logo_url}`;
        
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = logoUrl;
    }
  }, [settings?.logo_url]);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Navigate helper that triggers popstate
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const renderContent = () => {
    // Admin dashboard (hidden route)
    if (currentPath === '/nour') {
      return <AdminDashboard />;
    }
    if (currentPath === '/about') return <About />;
    if (currentPath === '/privacy') return <PrivacyPolicy />;
    if (currentPath === '/terms') return <TermsOfUse />;
    if (currentPath === '/editorial') return <EditorialPolicy />;

    if (currentPath === '/articles') {
      return <ArticlesPage articles={articles} />;
    }
    if (currentPath.startsWith('/category/')) {
      const slug = currentPath.split('/').pop() || '';
      // Map URL slug → Arabic category name (as stored in DB `category` field)
      const CATEGORY_MAP: Record<string, string> = {
        'opinion': 'مقالات الرأي',
        'opinion-corner': 'مقالات الرأي',
        'free-pens': 'أقلام حرة',
        'intellectual': 'قضايا فكرية',
        'dialogues': 'حوارات',
        'interviews': 'حوارات',
        'news': 'أخبار',
        'economy': 'اقتصاد',
        'statements': 'البيانات',
        'investigations': 'تحقيقات',
        'revolution-square': 'ساحة الثورة',
      };
      return (
        <ArticlesPage
          preSelectedCategory={CATEGORY_MAP[slug] || 'الكل'}
          articles={articles}
        />
      );
    }
    if (currentPath === '/contact') return <Contact />;
    if (currentPath === '/videos') return <Videos />;
    if (currentPath.startsWith('/author/')) return <Author />;
    if (currentPath.startsWith('/article/')) {
      const id = currentPath.split('/').pop();
      const filtered = (articles || []).filter(Boolean);
      const article = filtered.find((a) => a && String(a.id) === id) || filtered[0];
      return <ArticleDetail article={article} allArticles={filtered} />;
    }
    return <HomePage articles={articles} isLoading={isLoading} />;
  };

  const isAdmin = currentPath === '/nour';

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 transition-colors" dir={dir}>
      {!isAdmin && <TopBar />}
      {!isAdmin && <Navbar />}
      <main>
        <Suspense fallback={<PageLoader />}>
          {renderContent()}
        </Suspense>
      </main>
      {!isAdmin && <Footer />}
    </div>
  );
}

function HomePage({
  articles,
  isLoading,
}: {
  articles: Article[];
  isLoading: boolean;
}) {
  return (
    <>
      <Banner />
      <TopSelectedArticles isLoading={isLoading} />
      <SecondaryHeroSlider />
      <FieldOfRevolutionSection articles={articles} isLoading={isLoading} />

      <Section
        title="أحدث المقالات"
        articles={articles.slice(0, 4)}
        variant="grid"
        href="/articles"
        isLoading={isLoading}
      />

      <FeaturedCategories />

      <Section
        title="الحوارات"
        articles={articles
          .filter((a) => matchCategory(a, 'حوارات', ['Dialogues', 'Interviews', 'الحوارات']))
          .slice(0, 4)}
        variant="interviews"
        fullWidth={true}
        href="/category/dialogues"
        isLoading={isLoading}
      />

      <Section
        title="قضايا فكرية"
        articles={articles.filter((a) =>
          matchCategory(a, 'قضايا فكرية', ['Intellectual Issues', 'Intellectual', 'القضايا الفكرية'])
        )}
        variant="intellectual"
        href="/category/intellectual"
        isLoading={isLoading}
      />

      <Section
        title="مقالات الرأي"
        articles={articles.filter((a) =>
          matchCategory(a, 'مقالات الرأي', ['Opinion Articles', 'Opinion Corner', 'Opinion', 'مقالات رأي', 'زاوية الرأي'])
        )}
        variant="opinion"
        fullWidth={true}
        href="/category/opinion"
        isLoading={isLoading}
      />

      <Section
        title="مقابلات"
        articles={articles
          .filter((a) => matchCategory(a, 'حوارات', ['Dialogues', 'Interviews', 'الحوارات']))
          .slice(0, 4)}
        variant="interviews"
        fullWidth={true}
        href="/category/interviews"
        isLoading={isLoading}
      />

      <VideoLibrary />

      <Section
        title="أقلام حرة"
        articles={articles
          .filter((a) => matchCategory(a, 'أقلام حرة', ['Free Pens', 'FreePens', 'أقلامحرة']))
          .slice(0, 4)}
        variant="pens"
        fullWidth={true}
        href="/category/free-pens"
        isLoading={isLoading}
      />

      <HomeNewsletter />
    </>
  );
}
